import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { QrCode, User as UserIcon, LogOut, MessageCircle, Settings, Users, Menu, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { NavLink } from './ui/NavLink';

const ADMIN_EMAIL = 'mikael.antoine.83@gmail.com';

export default function Navbar() {
  const { user, signOut, loading } = useAuth();
  const isAdmin = user?.email === ADMIN_EMAIL;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Empêcher le défilement du body quand le menu est ouvert
    document.body.style.overflow = !isMenuOpen ? 'hidden' : 'auto';
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  const renderNavItems = (isMobile: boolean = false) => (
    <>
      {isAdmin && (
        <NavLink 
          to="/admin" 
          icon={Settings}
          title="Administration"
          onClick={isMobile ? closeMenu : undefined}
          className={isMobile ? "w-full justify-start px-4" : ""}
        >
          <span className={isMobile ? "inline" : "hidden sm:inline"}>Admin</span>
        </NavLink>
      )}
      {isAdmin && (
        <NavLink 
          to="/admin/marketing" 
          icon={Settings}
          title="Marketing"
          onClick={isMobile ? closeMenu : undefined}
          className={isMobile ? "w-full justify-start px-4" : ""}
        >
          <span className={isMobile ? "inline" : "hidden sm:inline"}>Marketing</span>
        </NavLink>
      )}
      <NavLink 
        to="/all-profiles" 
        icon={Users}
        title="Tous les profils"
        onClick={isMobile ? closeMenu : undefined}
        className={isMobile ? "w-full justify-start px-4" : ""}
      >
        <span className={isMobile ? "inline" : "hidden sm:inline"}>Annuaire</span>
      </NavLink>
      <NavLink 
        to="/retours" 
        icon={MessageCircle}
        title="Donnez votre avis"
        onClick={isMobile ? closeMenu : undefined}
        className={isMobile ? "w-full justify-start px-4" : ""}
      >
        <span className={isMobile ? "inline" : "hidden sm:inline"}>Retours</span>
      </NavLink>
      <NavLink 
        to="/profile" 
        icon={UserIcon}
        title="Profil"
        onClick={isMobile ? closeMenu : undefined}
        className={isMobile ? "w-full justify-start px-4" : ""}
      >
        <span className={isMobile ? "inline" : "hidden sm:inline"}>Profil</span>
      </NavLink>
    </>
  );

  return (
    <nav className="bg-primary-600 text-white relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link 
            to="/" 
            className="flex items-center space-x-2 hover:text-gray-200 transition-colors"
            onClick={closeMenu}
          >
            <Icon icon={QrCode} size="lg" />
            <span className="font-bold text-xl hidden sm:inline">vCard Pro</span>
          </Link>
          
          {user ? (
            <>
              {/* Navigation desktop */}
              <div className="hidden md:flex md:items-center md:space-x-4">
                {renderNavItems()}
                <Button
                  onClick={signOut}
                  disabled={loading}
                  variant="outline"
                  size="sm"
                  icon={LogOut}
                  className="text-white hover:text-primary-600 border-white hover:border-white hover:bg-white"
                >
                  <span className="hidden sm:inline">
                    {loading ? 'Déconnexion...' : 'Déconnexion'}
                  </span>
                </Button>
              </div>

              {/* Bouton menu mobile */}
              <button
                onClick={toggleMenu}
                className="md:hidden p-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </>
          ) : (
            <Button
              as={Link}
              to="/login"
              variant="outline"
              size="sm"
              icon={UserIcon}
              iconClassName="w-8 h-8 sm:w-6 sm:h-6"
              className="bg-white text-primary-600 hover:bg-white/90 border-white"
              onClick={closeMenu}
            >
              <span className="hidden sm:inline">Connexion</span>
            </Button>
          )}
        </div>
      </div>

      {/* Menu mobile */}
      {user && (
        <div
          className={`
            fixed inset-0 bg-primary-600 transition-transform duration-300 ease-in-out md:hidden
            ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
          style={{ top: '64px' }} // Hauteur de la navbar
        >
          <div className="flex flex-col py-4 space-y-1">
            {renderNavItems(true)}
            <div className="px-4 pt-4 border-t border-primary-500">
              <Button
                onClick={() => {
                  closeMenu();
                  signOut();
                }}
                disabled={loading}
                variant="outline"
                size="lg"
                icon={LogOut}
                className="w-full justify-center text-white hover:text-primary-600 border-white hover:border-white hover:bg-white"
              >
                {loading ? 'Déconnexion...' : 'Déconnexion'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}